import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import apiService from '../../services/ApiService';
import type { CreateEventPayload, EntryMode, EventStatus } from '../../types/event';
import {
  entryModeDescription,
  entryModeLabel,
  isPaidEntryMode,
} from '../../utils/registrationStatus';
import Button from '../ui/Button';
import PageBackLink from '../ui/PageBackLink';
import { PageHeader } from '../ui/PageHeader';

/**
 * Create-event form (`/organizer/events/new`).
 */
const CreateEventForm: React.FC = () => {
  const navigate = useNavigate();

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [location, setLocation] = useState('');
  const [city, setCity] = useState('');
  const [stateRegion, setStateRegion] = useState('');
  const [startAt, setStartAt] = useState('');
  const [endAt, setEndAt] = useState('');
  const [status, setStatus] = useState<EventStatus>('published');
  const [isPublic, setIsPublic] = useState(true);
  const [entryMode, setEntryMode] = useState<EntryMode>('open_free');
  const [paidEntryEnabled, setPaidEntryEnabled] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const flags = await apiService.getPaidFeatureFlags();
        setPaidEntryEnabled(Boolean(flags.paid_vehicle_entry_enabled));
      } catch {
        setPaidEntryEnabled(false);
      }
    })();
  }, []);

  const inputClass = 'input';
  const labelClass = 'input-label';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) {
      setError('Title is required');
      return;
    }

    const payload: CreateEventPayload = {
      title: title.trim(),
      status,
      is_public: isPublic,
      entry_mode: entryMode,
    };
    if (description.trim()) payload.description = description.trim();
    if (location.trim()) payload.location = location.trim();
    if (city.trim()) payload.city = city.trim();
    if (stateRegion.trim()) payload.state = stateRegion.trim();
    if (startAt) payload.start_at = startAt;
    if (endAt) payload.end_at = endAt;

    try {
      setSubmitting(true);
      setError(null);
      const event = await apiService.createEvent(payload);
      navigate(`/organizer/events/${event.id}`);
    } catch (err: any) {
      console.error('Failed to create event:', err);
      setError(err.message || 'Failed to create event');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-2xl">
        <PageBackLink label="All events" to="/organizer" className="mb-4" />
        <PageHeader
          title="New event"
          description="Create the event, then set Pricing/Tickets and publish from the event page."
          className="mb-6"
        />

        {error && (
          <div className="bg-red-900/20 border border-red-700 text-red-300 rounded-lg px-4 py-3 mb-4 text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="form-section space-y-5">
          <div>
            <label htmlFor="event-title" className={labelClass}>
              Title <span className="text-red-400">*</span>
            </label>
            <input
              id="event-title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className={inputClass}
              placeholder="e.g. Rocky Mountain JDM Meet"
              maxLength={150}
              required
            />
          </div>

          <div>
            <label htmlFor="event-description" className={labelClass}>Description</label>
            <textarea
              id="event-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className={inputClass}
              rows={4}
              placeholder="What is this event about?"
            />
          </div>

          <div>
            <label htmlFor="event-entry-mode" className={labelClass}>Registration mode</label>
            <select
              id="event-entry-mode"
              value={entryMode}
              onChange={(e) => {
                const next = e.target.value as EntryMode;
                if (isPaidEntryMode(next) && !paidEntryEnabled) return;
                setEntryMode(next);
              }}
              className={inputClass}
            >
              <option value="open_free">{entryModeLabel('open_free')}</option>
              <option value="pre_approval_free">{entryModeLabel('pre_approval_free')}</option>
              {paidEntryEnabled ? (
                <>
                  <option value="pre_approval_paid">{entryModeLabel('pre_approval_paid')}</option>
                  <option value="open_paid">{entryModeLabel('open_paid')}</option>
                </>
              ) : null}
            </select>
            <p className="text-xs text-gray-500 mt-1">{entryModeDescription(entryMode)}</p>
            {isPaidEntryMode(entryMode) ? (
              <p className="text-xs text-amber-300/90 mt-1">
                After create, open the event’s Pricing tab to set the entry fee before checkout will work.
              </p>
            ) : null}
            {!paidEntryEnabled ? (
              <p className="text-xs text-gray-600 mt-1">
                Paid modes appear after payout setup is enabled for your account.
              </p>
            ) : null}
          </div>

          <div>
            <label htmlFor="event-location" className={labelClass}>Venue / location</label>
            <input
              id="event-location"
              type="text"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              className={inputClass}
              placeholder="e.g. Front Range Speedway"
              maxLength={255}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="event-city" className={labelClass}>City</label>
              <input
                id="event-city"
                type="text"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                className={inputClass}
                maxLength={100}
              />
            </div>
            <div>
              <label htmlFor="event-state" className={labelClass}>State / region</label>
              <input
                id="event-state"
                type="text"
                value={stateRegion}
                onChange={(e) => setStateRegion(e.target.value)}
                className={inputClass}
                maxLength={100}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="event-start" className={labelClass}>Starts</label>
              <input
                id="event-start"
                type="datetime-local"
                value={startAt}
                onChange={(e) => setStartAt(e.target.value)}
                className={inputClass}
              />
            </div>
            <div>
              <label htmlFor="event-end" className={labelClass}>Ends</label>
              <input
                id="event-end"
                type="datetime-local"
                value={endAt}
                onChange={(e) => setEndAt(e.target.value)}
                className={inputClass}
              />
            </div>
          </div>

          <div>
            <label htmlFor="event-status" className={labelClass}>Status</label>
            <select
              id="event-status"
              value={status}
              onChange={(e) => {
                const next = e.target.value as EventStatus;
                setStatus(next);
                if (next === 'published') setIsPublic(true);
              }}
              className={inputClass}
            >
              <option value="draft">Draft</option>
              <option value="published">Published</option>
            </select>
          </div>

          <label className="flex items-center gap-2 text-sm text-gray-300">
            <input
              type="checkbox"
              checked={isPublic}
              onChange={(e) => setIsPublic(e.target.checked)}
              className="checkbox-control"
            />
            Make this event publicly viewable
          </label>

          <div className="flex items-center gap-3 pt-2">
            <Button type="submit" variant="primary" loading={submitting}>
              Create event
            </Button>
            <Button type="button" variant="ghost" onClick={() => navigate('/organizer')} disabled={submitting}>
              Cancel
            </Button>
          </div>
        </form>
    </div>
  );
};

export default CreateEventForm;
